import * as React from "react";
import * as z from "zod";

import { AddressByMapbox, AddressComponent } from "@/components/AddressComponent";
import { ArrowIcon, EditIcon } from "@/components/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { ICoordinates, LocationModalState } from "@/types/address.type";

import { Button } from "@/components/ui/button";
import { DecreeCategory } from "@/types/gql/graphql";
import { DecreeMultipleSelect } from "@/components/form-control/DecreeMultipleSelect";
import { Form } from "@/components/ui/form";
import { PermitLocation } from "types/decree.type";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface PermitLocationModalProps {
  coordinates?: ICoordinates;
  permitLocation: PermitLocation;
  onSave: (input: PermitLocation) => Promise<void>;
  onClose: () => void;
  permitLocationModalState: keyof typeof LocationModalState;
  setPermitLocationModalState: React.Dispatch<React.SetStateAction<keyof typeof LocationModalState>>;
  setPermitLocationDetail: (value: PermitLocation) => void;
}

const permitLocationSchema = z.object({
  name: z.string().optional(),
  address: z.object({
    streetNumber: z.string().min(1, "Street number is required"),
    streetName: z.string().min(1, "Street name is required"),
    city: z.string().min(1, "City is required"),
    province: z.string().optional().nullable(),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
  }),
  selectedDecrees: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .length(1, "Please select exactly one decree"),
});

type PermitLocationFormValues = z.infer<typeof permitLocationSchema>;

export const PermitLocationModal = ({
  permitLocation,
  onClose,
  onSave,
  permitLocationModalState,
  setPermitLocationModalState,
  setPermitLocationDetail,
  coordinates,
}: PermitLocationModalProps) => {
  const form = useForm<PermitLocationFormValues>({
    resolver: zodResolver(permitLocationSchema),
    defaultValues: {
      ...permitLocation,
    },
    mode: "all",
  });

  const {
    formState: { isSubmitting },
  } = form;

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (input: PermitLocationFormValues) => {
    const formattedInput: PermitLocation = {
      id: permitLocation?.id,
      address: input.address,
      position: permitLocation.position,
      selectedDecrees: input.selectedDecrees,
    };

    await onSave(formattedInput);
    handleClose();
  };

  const handleMinimizeModal = () => {
    setPermitLocationModalState(LocationModalState.MINIMIZE);
  };

  const handleShowModal = () => {
    setPermitLocationModalState(LocationModalState.SHOW);
  };

  const modalMinimize = permitLocationModalState === LocationModalState.MINIMIZE;

  const handleSelectPlace = ({ geometry }: AddressByMapbox) => {
    setPermitLocationDetail({
      ...permitLocation,
      position: geometry ?? permitLocation.position,
    });
  };
  return (
    <>
      <Dialog open={!modalMinimize}>
        <DialogContent className="max-h-[calc(100vh-5em)] overflow-auto">
          <DialogHeader>
            <DialogTitle>{`${permitLocation?.id ? "Edit" : "Add"} Permit location`}</DialogTitle>
            <DialogDescription>Enter the location details for the permit</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <DecreeMultipleSelect categories={[DecreeCategory.Permit]} required />
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold">Location:</span>
                  {permitLocation?.position && (
                    <Button className="text-blue-500" onClick={handleMinimizeModal} variant="ghost" type="button">
                      {permitLocation?.position.coordinates.toString()}
                    </Button>
                  )}
                  <EditIcon className="w-5 h-5 cursor-pointer fill-black" onClick={handleMinimizeModal} />
                </div>
                <AddressComponent onSelectAddress={handleSelectPlace} coordinates={coordinates} />
              </div>

              <DialogFooter className="sm:justify-between">
                <Button onClick={handleClose} variant="secondary" type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Button
        className={clsx("absolute bottom-12 right-4 shadow-md animate-bounce", modalMinimize ? "flex" : "hidden")}
        onClick={handleShowModal}
        variant="outline"
      >
        <span>Back to edit permit location</span>
        <ArrowIcon className="w-3 h-3 rotate-90" />
      </Button>
    </>
  );
};
